import { cloneDeep } from 'lodash';
import { datadogLogs } from '@datadog/browser-logs';
import { getProcessEnvUrl } from './getUrl';
import { getShiftedDatetimeHeader } from './shiftedDateTime';
import { LOGIN_ROUTE } from './constants';
import { routes } from '../routes';
import { ValidationError } from '../api';
import axios, { AxiosRequestHeaders } from 'axios';

function getTokenFromSessionStorage() {
  return sessionStorage.getItem('token');
}

const axiosInstance = axios.create({
  baseURL: getProcessEnvUrl('WEB_API'),
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  } as AxiosRequestHeaders,
});

// This is a request interceptor that will run before every request.
// It will add the Authorization header with the token from sessionStorage.
axiosInstance.interceptors.request.use(function getInternalAxiosRequestConfig(
  config,
) {
  // Already has Authorization header
  if (config.headers.Authorization) {
    return config;
  }

  const token = getTokenFromSessionStorage();
  // No token in sessionStorage
  if (!token) {
    return config;
  }

  const newConfig = cloneDeep(config);
  newConfig.headers.Authorization = `Basic ${token}`;

  // Add time header'
  const shiftedDateTime = getShiftedDatetimeHeader();
  if (shiftedDateTime.time) newConfig.headers.time = shiftedDateTime.time;

  return newConfig;
});

axiosInstance.interceptors.response.use(
  function getInternalAxiosResponseConfig(response) {
    return response;
  },
  function onRejected(error) {
    if (error.response?.status === 401) {
      // Wrong credentials from login endpoint return 401, returns error as normal error
      if (error.response.request.responseURL.includes('/token/basic')) {
        return Promise.reject(error);
      }

      sessionStorage.removeItem('token');

      const loginSessionExpiredRoute = `${LOGIN_ROUTE}?errorMessage=session_expired`;
      const logoutInProcess = sessionStorage.getItem('logoutInProcess');

      if (!logoutInProcess && window.location.pathname !== routes.FirstLoan) {
        window.location.assign(loginSessionExpiredRoute);
      }
    }

    const fieldErrors =
      error?.response?.data?.errors || error?.errors || error?.fieldErrors;

    if (error?.response?.status === 400 && fieldErrors) {
      const validationError = new ValidationError(
        fieldErrors,
        error?.response?.status,
      );
      return Promise.reject(validationError);
    }

    if (error?.response?.status >= 500) {
      datadogLogs.logger.error('[Axios interceptor] webapi error', error);
    }

    Promise.reject(error);
  },
);

export default axiosInstance;
